/**
 * Diff highlight plugin utilities for BlockNote editors
 *
 * Provides utilities for applying diff decorations to BlockNote editor instances
 */

import type { BlockNoteEditor } from '@blocknote/core';

import {
  createDiffPlugin,
  type DiffOperation,
  diffPluginKey,
} from '../../components/BlockNoteDiffPlugin';

export {
  createDiffPlugin,
  type DiffOperation,
  diffPluginKey,
} from '../../components/BlockNoteDiffPlugin';

/**
 * Applies the diff highlight plugin to a BlockNote editor instance
 *
 * @param editor - The BlockNote editor instance
 * @param diff - Array of diff operations from backend [[operation, text], ...]
 *               where operation is -1 (delete), 0 (unchanged), 1 (insert)
 */
export const applyDiffPlugin = (
  editor: BlockNoteEditor,
  diff: DiffOperation[],
): void => {
  // Access the underlying TipTap/ProseMirror instance
  // BlockNote uses TipTap under the hood
  // This requires accessing internal APIs, so we disable linting for this section
  /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
  const tiptapEditor = (editor as any)._tiptapEditor;
  if (!tiptapEditor || !tiptapEditor.view) {
    console.warn('BlockNote editor TipTap instance not available');
    return;
  }

  try {
    const state = tiptapEditor.state;

    // Filter out any existing diff plugin
    // ProseMirror plugins store their key in spec.key, not directly on the plugin
    const existingPlugins = state.plugins.filter((p: any) => {
      return p.spec?.key !== diffPluginKey;
    });

    // Create new plugin
    const plugin = createDiffPlugin(diff);

    // Create new state with updated plugins
    const newState = state.reconfigure({
      plugins: [...existingPlugins, plugin],
    });

    tiptapEditor.view.updateState(newState);
  } catch (error) {
    console.error('Error applying diff plugin:', error);
  }
  /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
};

/**
 * Removes the diff highlight plugin from a BlockNote editor instance
 *
 * @param editor - The BlockNote editor instance
 */
export const removeDiffPlugin = (
  editor: BlockNoteEditor,
): void => {
  /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
  const tiptapEditor = (editor as any)._tiptapEditor;
  if (!tiptapEditor || !tiptapEditor.view) {
    console.warn('BlockNote editor TipTap instance not available');
    return;
  }

  try {
    const state = tiptapEditor.state;

    // Filter out the diff plugin
    // ProseMirror plugins store their key in spec.key, not directly on the plugin
    const existingPlugins = state.plugins.filter((p: any) => {
      return p.spec?.key !== diffPluginKey;
    });

    // Create new state without diff plugin
    const newState = state.reconfigure({
      plugins: existingPlugins,
    });

    tiptapEditor.view.updateState(newState);
  } catch (error) {
    console.error('Error removing diff plugin:', error);
  }
  /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
};
